import React, { useState } from 'react';
import populateFirestore from '../scripts/populateFirestore';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import runFirebaseTest from '../scripts/testFirebase';

const AdminUtility = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    setLogs(prevLogs => [...prevLogs, { time: new Date().toLocaleTimeString(), message }]);
  };

  const handlePopulateFirestore = async () => {
    try {
      setLoading(true);
      setResult(null);
      setLogs([]);
      
      addLog('Starting database population process...');
      
      // Check if Firestore is connected
      try {
        addLog('Testing Firestore connection...');
        const testQuery = await getDocs(collection(db, 'test'));
        addLog(`Firestore connection successful. Test collection returned ${testQuery.docs.length} documents.`);
      } catch (error) {
        addLog(`Firestore connection test error: ${error.message}`);
        // Continue anyway as the collection might not exist
      }
      
      // Run the populate function
      addLog('Running populate function...');
      const stats = await populateFirestore();
      
      addLog(`Population complete. Testimonials: ${stats?.testimonialsCount || 0}, Case Studies: ${stats?.caseStudiesCount || 0}`);
      
      setResult({
        success: true,
        message: `Database successfully populated with ${stats?.testimonialsCount || 0} testimonials and ${stats?.caseStudiesCount || 0} case studies!`,
        details: stats
      });
    } catch (error) {
      console.error('Error in population:', error);
      addLog(`Error during population: ${error.message}`);
      setResult({
        success: false,
        message: `Error: ${error.message}`,
        error: error
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDatabase = async () => {
    try {
      setLoading(true);
      addLog('Checking database collections...');
      
      const testimonialsSnapshot = await getDocs(collection(db, 'testimonials'));
      const caseStudiesSnapshot = await getDocs(collection(db, 'caseStudies'));
      
      addLog(`Found ${testimonialsSnapshot.docs.length} testimonials`);
      addLog(`Found ${caseStudiesSnapshot.docs.length} case studies`);
      
      // Show first document as sample if exists
      if (testimonialsSnapshot.docs.length > 0) {
        const sampleDoc = testimonialsSnapshot.docs[0].data();
        addLog(`Sample testimonial: ${sampleDoc.name} - "${sampleDoc.content.substring(0, 50)}..."`);
      }
      
    } catch (error) {
      console.error('Error checking database:', error);
      addLog(`Error checking database: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFirebaseTest = async () => {
    try {
      setLoading(true);
      setLogs([]);
      addLog('Starting Firebase connection test...');
      
      const results = await runFirebaseTest();
      
      addLog(`Test completed - Connection: ${results.connectionSuccess ? 'SUCCESS' : 'FAILED'}`);
      addLog(`Read operation: ${results.readSuccess ? 'SUCCESS' : 'FAILED'}`);
      addLog(`Write operation: ${results.writeSuccess ? 'SUCCESS' : 'FAILED'}`);
      addLog(`Delete operation: ${results.deleteSuccess ? 'SUCCESS' : 'FAILED'}`);
      
      if (results.errors.length > 0) {
        addLog('===== ERRORS =====');
        results.errors.forEach(error => {
          addLog(`[${error.operation}] ${error.message}`);
        });
      }
      
      if (results.connectionSuccess) {
        setResult({
          success: true,
          message: 'Firebase connection test successful!',
          details: results
        });
      } else {
        setResult({
          success: false,
          message: 'Firebase connection test failed. See logs for details.',
          details: results
        });
      }
    } catch (error) {
      console.error('Test error:', error);
      addLog(`Test error: ${error.message}`);
      setResult({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 px-4 min-h-screen">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">Admin Utilities</h1>
          
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Firebase Connection</h2>
            <p className="mb-4 text-gray-600">
              Test your Firebase connection before attempting to populate the database.
            </p>
            
            <button
              onClick={handleFirebaseTest}
              disabled={loading}
              className={`px-6 py-2 rounded-lg font-medium mb-6 ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {loading ? 'Testing...' : 'Test Firebase Connection'}
            </button>
          </div>
          
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Database Operations</h2>
            <p className="mb-4 text-gray-600">
              Use these utilities to manage your Firestore database content for testimonials and case studies.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handlePopulateFirestore}
                disabled={loading}
                className={`px-6 py-2 rounded-lg font-medium ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}
              >
                {loading ? 'Processing...' : 'Populate Firestore Database'}
              </button>
              
              <button
                onClick={handleCheckDatabase}
                disabled={loading}
                className="px-6 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
              >
                Check Database Content
              </button>
            </div>
          </div>
          
          {result && (
            <div className={`p-4 rounded-lg mb-6 ${
              result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              <h3 className="font-semibold mb-2">{result.success ? 'Success' : 'Error'}</h3>
              <p>{result.message}</p>
              {result.details && (
                <div className="mt-2 text-sm">
                  <div>Testimonials Count: {result.details.testimonialsCount}</div>
                  <div>Case Studies Count: {result.details.caseStudiesCount}</div>
                </div>
              )}
            </div>
          )}
          
          {logs.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Process Logs</h3>
              <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto text-sm font-mono">
                {logs.map((log, index) => (
                  <div key={index} className="mb-1">
                    <span className="text-gray-500">[{log.time}]</span> {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">Troubleshooting</h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li>Verify your Firebase configuration in <code className="bg-gray-100 px-2 py-1 rounded">src/firebase/config.js</code></li>
              <li>Make sure you've created a Firestore database in your Firebase project</li>
              <li>Check that your security rules allow reading and writing to the collections</li>
              <li>If you're getting permissions errors, try setting your database to test mode temporarily</li>
              <li>Check the browser console (F12) for detailed error messages</li>
            </ol>
            
            <h4 className="text-md font-semibold mt-4 mb-2 text-gray-800">Security Rules</h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="mb-2">For testing, you can use these security rules in your Firebase console:</p>
              <pre className="text-sm bg-gray-100 p-2 rounded">
{`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /{document=**} {
      allow read, write: if true;
    }
  }
}`}
              </pre>
              <p className="mt-2 text-sm text-yellow-600">Warning: These rules allow anyone to read and write to your database. Use for testing only.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUtility; 